/**
 * Profil.
 *
 * Profilbild und E-Mail-Adresse lassen sich hier ändern. Ein Passwortbereich
 * fehlt bewusst: die Anmeldung läuft über das Microsoft-Konto, dort wird auch
 * das Passwort verwaltet.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal } from '../komponenten/Modal';
import type {
  AmpelSchluessel,
  DarstellungAuswahl,
  Nutzer,
  Profil,
  ThemaSchluessel,
} from '../typen';
import './profil.css';

export function ProfilModal() {
  const { modalSchliessen, nutzer, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'bild' | 'mail' | 'darstellung' | 'ordner' | 'anmeldung'>(
    'bild',
  );
  const [mail, setMail] = useState('');
  const [meldung, setMeldung] = useState<{ text: string; gut: boolean } | null>(null);

  const abfrage = useQuery({
    queryKey: ['profil'],
    queryFn: () => api.hole<Profil>('/profil'),
  });

  useEffect(() => {
    if (abfrage.data && !mail) setMail(abfrage.data.email);
  }, [abfrage.data, mail]);

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['ich'] });
    void queryClient.invalidateQueries({ queryKey: ['profil'] });
    void queryClient.invalidateQueries({ queryKey: ['meta'] });
  }

  const bildSetzen = useMutation({
    mutationFn: (bild: string) => api.ersetze<Nutzer>('/profil/bild', { bild }),
    onSuccess: () => {
      neuLaden();
      toastZeigen('Profilbild gespeichert.');
    },
    onError: (f) =>
      setMeldung({ text: f instanceof Error ? f.message : 'Speichern fehlgeschlagen.', gut: false }),
  });

  const bildWeg = useMutation({
    mutationFn: () => api.loesche<Nutzer>('/profil/bild'),
    onSuccess: neuLaden,
  });

  const mailSetzen = useMutation({
    mutationFn: () => api.aendere<{ meldung: string }>('/profil/email', { email: mail.trim() }),
    onSuccess: (a) => {
      neuLaden();
      setMeldung({ text: a.meldung, gut: true });
    },
    onError: (f) =>
      setMeldung({ text: f instanceof Error ? f.message : 'Speichern fehlgeschlagen.', gut: false }),
  });

  // Die Auswahl kommt vom Server, damit Beschriftung und Farbproben nicht an
  // zwei Stellen gepflegt werden müssen.
  const darstellung = useQuery({
    queryKey: ['darstellung'],
    queryFn: () => api.hole<DarstellungAuswahl>('/profil/darstellung'),
    staleTime: Infinity,
  });

  // Kein Speichern-Knopf: Die Wirkung ist sofort sichtbar, ein Knopf daneben
  // wäre nur eine zweite Bestätigung für etwas, das man schon sieht.
  const darstellungSetzen = useMutation({
    mutationFn: (wahl: { thema?: ThemaSchluessel; ampel?: AmpelSchluessel; outlookAmPc?: boolean }) =>
      api.aendere<Nutzer>('/profil/darstellung', wahl),
    onSuccess: neuLaden,
    onError: (f) =>
      setMeldung({ text: f instanceof Error ? f.message : 'Speichern fehlgeschlagen.', gut: false }),
  });

  const p = abfrage.data;

  return (
    <Modal
      titel="Profil"
      untertitel={nutzer ? `${nutzer.name} · ${nutzer.rolle}` : ''}
      breite={560}
      zeigeSchliessen
      onSchliessen={modalSchliessen}
      fuss={
        <button type="button" className="pr-fertig" onClick={modalSchliessen}>
          Fertig
        </button>
      }
    >
      <div className="pr-tabs">
        {(
          [
            { k: 'bild', l: 'Profilbild' },
            { k: 'mail', l: 'E-Mail' },
            { k: 'darstellung', l: 'Darstellung' },
            { k: 'ordner', l: 'Ordner öffnen' },
            { k: 'anmeldung', l: 'Anmeldung' },
          ] as const
        ).map((t) => (
          <button
            key={t.k}
            type="button"
            className={`pr-tab ${tab === t.k ? 'aktiv' : ''}`}
            onClick={() => {
              setTab(t.k);
              setMeldung(null);
            }}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'bild' && (
        <div className="pr-bildbereich">
          <span
            className="eh-avatar pr-bild"
            style={nutzer?.profilbild ? { backgroundImage: `url(${nutzer.profilbild})` } : undefined}
          >
            {nutzer?.profilbild ? '' : nutzer?.initialen}
          </span>
          <div className="pr-bildaktionen">
            <label className="pr-knopf-sek">
              Bild auswählen
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const datei = e.target.files?.[0];
                  if (!datei) return;
                  const leser = new FileReader();
                  leser.onload = () => bildSetzen.mutate(String(leser.result));
                  leser.readAsDataURL(datei);
                  e.target.value = '';
                }}
              />
            </label>
            <button type="button" className="pr-knopf-sek" onClick={() => bildWeg.mutate()}>
              Bild entfernen
            </button>
            <div className="modal-hinweis">
              PNG, JPG, WEBP oder GIF bis 2 MB. Das Bild erscheint sofort in der Seitenleiste.
              Künftig sollen die Bilder aus dem Benutzerverzeichnis kommen.
            </div>
          </div>
        </div>
      )}

      {tab === 'mail' && (
        <>
          <label className="modal-feld">
            <span>E-Mail-Adresse</span>
            <input
              className="modal-eingabe"
              value={mail}
              onChange={(e) => {
                setMail(e.target.value);
                setMeldung(null);
              }}
            />
          </label>
          <div className="modal-hinweis">
            Aus diesem Postfach gehen die automatischen Erinnerungen raus, wenn du Versender bist.
            Sie muss dem Microsoft-Konto entsprechen.
          </div>
          <div>
            <button
              type="button"
              className="pr-knopf-prim"
              disabled={mailSetzen.isPending}
              onClick={() => mailSetzen.mutate()}
            >
              {mailSetzen.isPending ? 'Wird gespeichert …' : 'E-Mail speichern'}
            </button>
          </div>
        </>
      )}

      {tab === 'anmeldung' && p && (
        <>
          <div className={`eh-banner ${p.passwortVerwaltung.extern ? 'eh-banner--blau' : 'eh-banner--gelb'}`}>
            {p.passwortVerwaltung.hinweis}
          </div>
          {p.passwortVerwaltung.url && (
            <div>
              <a
                href={p.passwortVerwaltung.url}
                target="_blank"
                rel="noreferrer"
                className="pr-knopf-prim pr-link"
              >
                Microsoft-Kontosicherheit öffnen ↗
              </a>
            </div>
          )}
          <div className="modal-hinweis">
            Wer welchen Bereich sieht, legt die Geschäftsführung im Admin Center fest.
          </div>
        </>
      )}

      {tab === 'ordner' && (
        <>
      {/*
        Ordner im Explorer öffnen.
        Steht im Profil und nicht im Admin Center: Es ist eine Einrichtung je
        Rechner, keine je Firma. Wer an einem neuen Arbeitsplatz sitzt, braucht
        sie dort erneut – und soll sie selbst holen können, ohne zu fragen.
      */}
      <div className="pr-abschnitt">
        <div className="pr-abschnitt-titel">Ordner im Explorer öffnen</div>
        <p className="modal-hinweis" style={{ margin: '0 0 10px' }}>
          Damit ein Klick auf das <strong>Ordner-Symbol</strong> den Vorgangsordner direkt im
          Windows-Explorer öffnet, muss auf diesem Rechner einmal eine Kleinigkeit eingerichtet
          werden. Ohne sie passiert beim Klick nichts – dann zeigt Kora den Pfad zum Einfügen.
        </p>

        <a className="pr-knopf" href="/api/oeffner/einrichten.cmd">
          Einrichtung herunterladen
        </a>

        <ol className="pr-schritte">
          <li>Datei herunterladen und <strong>doppelt anklicken</strong>.</li>
          <li>
            Windows warnt möglicherweise vor einer unbekannten Datei – auf „Weitere Informationen"
            und dann „Trotzdem ausführen".
          </li>
          <li>Browser einmal schließen und neu öffnen.</li>
        </ol>

        <p className="modal-hinweis" style={{ margin: 0 }}>
          Es werden keine Administratorrechte gebraucht, und es wird nichts installiert – nur für
          dich eingetragen, dass Windows die Links aus Kora versteht. Wo dein Ablageordner
          liegt, erfragt das Skript bei OneDrive; der Pfad ist auf jedem Rechner ein anderer.
        </p>
      </div>
        </>
      )}

      {tab === 'darstellung' && (
        <>
          <div className="pr-wahlgruppe">
            <div className="pr-wahltitel">Farbschema</div>
            <div className="pr-wahlen">
              {(darstellung.data?.themen ?? []).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`pr-wahl ${nutzer?.thema === t.key ? 'aktiv' : ''}`}
                  onClick={() => darstellungSetzen.mutate({ thema: t.key as ThemaSchluessel })}
                >
                  <span className={`pr-probe pr-probe--${t.key}`} aria-hidden="true" />
                  <span className="pr-wahl-text">
                    <span className="pr-wahl-label">{t.label}</span>
                    <span className="pr-wahl-hinweis">{t.hinweis}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/*
            Wohin ein Montagetermin aufgeht (MONTAGEKALENDER.md 11.5).
            Steht hier und nicht im Admin Center: Es hängt am Rechner, nicht
            an der Firma – wer kein klassisches Outlook hat, schaltet es aus.
          */}
          <div className="pr-wahlgruppe">
            <div className="pr-wahltitel">Montagetermine öffnen</div>
            <div className="pr-wahlen">
              {[
                { an: true, label: 'Im Outlook am PC', hinweis: 'Braucht das klassische Outlook mit dem Kalender „MONTAGE - Termine".' },
                { an: false, label: 'Im Browser', hinweis: 'Funktioniert überall – auch mit dem neuen Outlook.' },
              ].map((w) => (
                <button
                  key={String(w.an)}
                  type="button"
                  className={`pr-wahl ${!!nutzer?.outlookAmPc === w.an ? 'aktiv' : ''}`}
                  onClick={() => darstellungSetzen.mutate({ outlookAmPc: w.an })}
                >
                  <span className="pr-wahl-text">
                    <span className="pr-wahl-label">{w.label}</span>
                    <span className="pr-wahl-hinweis">{w.hinweis}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="pr-wahlgruppe">
            <div className="pr-wahltitel">Farben der Liegezeit</div>
            <div className="pr-wahlen">
              {(darstellung.data?.ampeln ?? []).map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`pr-wahl ${nutzer?.ampel === a.key ? 'aktiv' : ''}`}
                  onClick={() => darstellungSetzen.mutate({ ampel: a.key as AmpelSchluessel })}
                >
                  <span className="pr-ampelprobe" aria-hidden="true">
                    {(a.proben ?? []).map((farbe) => (
                      <span key={farbe} style={{ background: farbe }} />
                    ))}
                  </span>
                  <span className="pr-wahl-text">
                    <span className="pr-wahl-label">{a.label}</span>
                    <span className="pr-wahl-hinweis">{a.hinweis}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="modal-hinweis">
            Gilt nur für dich und auf jedem Rechner, an dem du dich anmeldest. Die Liegezeit steht
            neben der Farbe immer auch als Zahl – wer die Töne nicht auseinanderhält, verliert
            dadurch keine Information.
          </div>
        </>
      )}

      {meldung && (
        <div
          className="pr-meldung"
          style={{ color: meldung.gut ? 'var(--eh-gruen-ampel)' : 'var(--eh-rot)' }}
        >
          {meldung.text}
        </div>
      )}
    </Modal>
  );
}
